import React, { useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { HardHat, Truck, Clock, Phone, DollarSign, Calculator, ArrowRight, CheckCircle2, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '../data/products';
const benefits = [{
  icon: DollarSign,
  title: 'Bulk Pricing',
  description: 'Volume discounts on all materials and dedicated account pricing for our regular partners.'
}, {
  icon: Clock,
  title: 'Priority Scheduling',
  description: 'Same-day and next-day delivery slots reserved specifically for contractors to keep jobs moving.'
}, {
  icon: Phone,
  title: 'Dedicated Support',
  description: 'Direct line to our dispatch team. No waiting in queue when you need materials fast.'
}, {
  icon: CheckCircle2,
  title: 'Flexible Billing',
  description: 'Net-30 terms available for qualified contractors to help manage cash flow.'
}, {
  icon: Truck,
  title: 'Job Site Delivery',
  description: 'Professional drivers who know how to navigate tight job sites and drop exactly where needed.'
}, {
  icon: Calculator,
  title: 'Material Calculator',
  description: 'Free project estimation and material planning assistance for complex jobs.'
}];
const steps = [{
  number: '01',
  title: 'Apply',
  description: 'Fill out the quick application form below with your company details and estimated volume.'
}, {
  number: '02',
  title: 'Get Approved',
  description: 'Our team will review your application and set up your account within 24 hours.'
}, {
  number: '03',
  title: 'Start Ordering',
  description: 'Access your discounted pricing and priority scheduling immediately upon approval.'
}];
const discountTiers = [{
  volume: '24-50 yards',
  discount: '5% Off',
  desc: 'Mid-size project discount'
}, {
  volume: '50-100 yards',
  discount: '10% Off',
  desc: 'Large project discount'
}, {
  volume: '100+ yards',
  discount: 'Call Us',
  desc: 'Custom bulk pricing'
}];
function RingTrace({
  elementSize,
  delay,
  duration,
  isInView,
  startAngle
}: {
  elementSize: number;
  delay: number;
  duration: number;
  isInView: boolean;
  startAngle: number;
}) {
  const svgSize = elementSize + 4;
  const radius = elementSize / 2 + 1;
  const circumference = 2 * Math.PI * radius;
  return <svg width={svgSize} height={svgSize} className="absolute pointer-events-none z-20" style={{
    top: '50%',
    left: '50%',
    transform: `translate(-50%, -50%) rotate(${startAngle}deg)`
  }} data-id="element-1556">
      <motion.circle cx={svgSize / 2} cy={svgSize / 2} r={radius} fill="none" stroke="rgba(255, 103, 77, 0.25)" strokeWidth={2} strokeLinecap="round" initial={{
      strokeDasharray: circumference,
      strokeDashoffset: circumference
    }} animate={isInView ? {
      strokeDashoffset: 0
    } : {
      strokeDashoffset: circumference
    }} transition={{
      duration,
      ease: [0.25, 0.1, 0.25, 1],
      delay
    }} data-id="element-1557" />
    </svg>;
}
export function Subcontractors() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    licenseNumber: '',
    estimatedVolume: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInView(stepsRef, {
    once: true,
    margin: '-100px'
  });
  const ringParams = useMemo(() => ({
    ringDelays: [0.5, 2.0, 0.8],
    ringDurations: [8, 10, 7],
    ringAngles: [45, 220, 310],
    mobileRingAngles: [120, 300, 30],
    lineDelays: [0, 1.5],
    lineDurations: [9, 11]
  }), []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitted(true);
    setIsSubmitting(false);
    setFormData({
      companyName: '',
      contactName: '',
      phone: '',
      email: '',
      licenseNumber: '',
      estimatedVolume: ''
    });
  };
  const inputClass = 'w-full bg-stone-100 dark:bg-[#2f2f2f] border border-stone-300 dark:border-[#4a4a4a] rounded-none px-4 py-3 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-[#ff674d]/50 focus:border-[#ff674d]/50 outline-none transition-all';
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#1f1f1f] transition-colors duration-300 pb-20" data-id="element-1558">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-[#2c2c2c]" data-id="element-1559">
        <div className="absolute inset-0" data-id="element-1560">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-60" data-id="element-1561">
            <source src="https://cdn.magicpatterns.com/uploads/8VPf3pCGeZMRfF2M5TnjGs/excavator.mov" type="video/mp4" data-id="element-1562" />
          </video>
          <div className="absolute inset-0 bg-black/60" data-id="element-1563" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1564">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#ff674d]/10 mb-8" data-id="element-1565">
            <HardHat className="w-8 h-8 text-[#ff674d]" data-id="element-1566" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1567">
            Contractor <span className="text-[#ff674d]" data-id="element-1568">Program</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-xl text-stone-200 max-w-2xl mx-auto font-medium font-sub leading-relaxed" data-id="element-1569">
            Partner with 253Gravel for bulk pricing, priority scheduling, and
            dedicated support to keep your projects running smoothly and
            profitably.
          </motion.p>
        </div>
      </section>

      {/* Volume Discount Tiers */}
      <section className="py-20 bg-white dark:bg-[#1f1f1f]" data-id="element-1570">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1571">
          <div className="text-center mb-12" data-id="element-1572">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1573">
              Volume Discount Tiers
            </h2>
            <p className="text-stone-600 dark:text-stone-400 text-lg" data-id="element-1574">
              Discounts applied automatically on qualifying orders.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto" data-id="element-1575">
            {discountTiers.map((tier, index) => <motion.div key={tier.volume} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-stone-50 dark:bg-[#2c2c2c] rounded-xl p-6 border border-stone-200 dark:border-[#3f3f3f] text-center" data-id="element-1576">
                <div className="text-stone-700 dark:text-stone-200 font-bold mb-2" data-id="element-1577">
                  {tier.volume}
                </div>
                <div className="text-2xl font-heading font-extrabold text-[#ff674d] mb-2" data-id="element-1578">
                  {tier.discount}
                </div>
                <div className="text-sm text-stone-600 dark:text-stone-300" data-id="element-1579">
                  {tier.desc}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-stone-50 dark:bg-[#2c2c2c]" data-id="element-1580">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1581">
          <div className="text-center mb-16" data-id="element-1582">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1583">
              Program Benefits
            </h2>
            <p className="text-stone-600 dark:text-stone-400 text-lg" data-id="element-1584">
              Everything you need to scale your business.
            </p>
          </div>
          <div className="space-y-16" data-id="element-1585">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1586">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white dark:bg-[#1f1f1f] rounded-2xl p-8 border border-stone-200 dark:border-[#3f3f3f] hover:border-[#ff674d]/30 transition-colors" data-id="element-1587">
                  <benefit.icon className="w-10 h-10 text-[#ff674d] mb-6" data-id="element-1588" />
                  <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-1589">
                    {benefit.title}
                  </h3>
                  <p className="text-stone-600 dark:text-stone-200 leading-relaxed" data-id="element-1590">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-[#2c2c2c]" data-id="element-1591">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1592">
          <div className="text-center mb-16" data-id="element-1593">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1594">
              How to Join
            </h2>
            <p className="text-stone-600 dark:text-stone-400 text-lg" data-id="element-1595">
              A simple process to get you started.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 relative" ref={stepsRef} data-id="element-1596">
            {/* Desktop Connecting Lines */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-[2px] bg-stone-200 dark:bg-[#3f3f3f]" data-id="element-1597">
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-y-0 left-0 rounded-full" style={{
              backgroundColor: 'rgba(255, 103, 77, 0.5)',
              boxShadow: '0 0 4px rgba(255, 103, 77, 0.2)'
            }} data-id="element-1598" />
              <motion.div initial={{
              width: '0%'
            }} animate={stepsInView ? {
              width: '50%'
            } : {
              width: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-y-0 left-[50%] rounded-full" style={{
              backgroundColor: 'rgba(255, 103, 77, 0.5)',
              boxShadow: '0 0 4px rgba(255, 103, 77, 0.2)'
            }} data-id="element-1599" />
            </div>

            {/* Mobile Connecting Lines */}
            <div className="md:hidden absolute top-0 bottom-0 left-8 w-[2px] bg-stone-200 dark:bg-[#3f3f3f]" data-id="element-1600">
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[0],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[0]
            }} className="absolute inset-x-0 top-0 rounded-full" style={{
              backgroundColor: 'rgba(255, 103, 77, 0.5)',
              boxShadow: '0 0 4px rgba(255, 103, 77, 0.2)'
            }} data-id="element-1601" />
              <motion.div initial={{
              height: '0%'
            }} animate={stepsInView ? {
              height: '50%'
            } : {
              height: '0%'
            }} transition={{
              duration: ringParams.lineDurations[1],
              ease: [0.25, 0.1, 0.25, 1],
              delay: ringParams.lineDelays[1]
            }} className="absolute inset-x-0 top-[50%] rounded-full" style={{
              backgroundColor: 'rgba(255, 103, 77, 0.5)',
              boxShadow: '0 0 4px rgba(255, 103, 77, 0.2)'
            }} data-id="element-1602" />
            </div>

            {steps.map((step, index) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            type: 'spring',
            stiffness: 100
          }} className="relative flex flex-col md:items-center pl-20 md:pl-0 h-full" data-id="element-1603">
                {/* Step Number Node */}
                <div className="absolute left-0 md:relative md:left-auto w-16 h-16 md:w-24 md:h-24 bg-stone-50 dark:bg-[#373737] border-4 border-white dark:border-[#2c2c2c] rounded-full flex items-center justify-center mb-8 shadow-[0_0_0_2px_rgba(255,69,0,0.2)] z-10 shrink-0" data-id="element-1604">
                  <span className="font-heading text-2xl md:text-3xl font-extrabold text-[#ff674d]" data-id="element-1605">
                    {step.number}
                  </span>
                  {/* Ring trace - desktop */}
                  <div className="hidden md:block" data-id="element-1606">
                    <RingTrace elementSize={96} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.ringAngles[index]} data-id="element-1607" />
                  </div>
                  {/* Ring trace - mobile */}
                  <div className="md:hidden" data-id="element-1608">
                    <RingTrace elementSize={64} delay={ringParams.ringDelays[index]} duration={ringParams.ringDurations[index]} isInView={stepsInView} startAngle={ringParams.mobileRingAngles[index]} data-id="element-1609" />
                  </div>
                </div>

                {/* Content Card */}
                <div className="bg-white dark:bg-gradient-to-b dark:from-[#4a4a4a] dark:to-[#3b3b3b] rounded-2xl p-8 w-full md:text-center border border-stone-200 dark:border-[#525252]/60 dark:border-b-[#333333]/60 hover:border-[#ff674d]/30 dark:hover:border-[#ff674d]/30 transition-colors duration-300 relative overflow-hidden group flex-1 flex flex-col shadow-sm dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_2px_8px_rgba(0,0,0,0.2)]" data-id="element-1610">
                  <span className="absolute -bottom-6 -right-4 font-heading text-8xl font-black text-stone-100 dark:text-[#4a4a4a]/30 group-hover:text-[#ff674d]/5 transition-colors duration-500 pointer-events-none select-none" data-id="element-1611">
                    {step.number}
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-4 relative z-10" data-id="element-1612">
                    {step.title}
                  </h3>
                  <p className="text-stone-800 dark:text-stone-300 leading-relaxed text-[15px] relative z-10" data-id="element-1613">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-stone-50 dark:bg-[#1f1f1f]" data-id="element-1614">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1615">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1616">
            {/* Image Column */}
            <div className="hidden lg:block relative" data-id="element-1617">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="sticky top-32 aspect-[3/4] rounded-2xl overflow-hidden border border-stone-200 dark:border-[#3f3f3f]" data-id="element-1618">
                <img src="https://cdn.magicpatterns.com/uploads/5XaxepFncit8gaVhvc86Zp/253_gravel_tacoma_premier_gravel_supplier.png" alt="253 Gravel Premier Supplier" className="w-full h-full object-cover" data-id="element-1619" />
              </motion.div>
            </div>

            {/* Form Column */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white dark:bg-[#3c3c3c] rounded-2xl p-8 md:p-12 border border-stone-200 dark:border-[#4a4a4a] shadow-xl" data-id="element-1620">
              <div className="text-center mb-10" data-id="element-1621">
                <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1622">
                  Apply for a Pro Account
                </h2>
                <p className="text-stone-600 dark:text-stone-300" data-id="element-1623">
                  Fill out the form below and our team will be in touch shortly.
                </p>
              </div>

              {isSubmitted ? <div className="flex flex-col items-center justify-center text-center py-12" data-id="element-1624">
                  <div className="w-16 h-16 bg-[#ff674d]/10 rounded-full flex items-center justify-center mb-6" data-id="element-1625">
                    <CheckCircle2 className="w-8 h-8 text-[#ff674d]" data-id="element-1626" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-3" data-id="element-1627">
                    Application Received!
                  </h3>
                  <p className="text-stone-600 dark:text-stone-300 mb-8 max-w-md" data-id="element-1628">
                    Thank you for applying to the 253Gravel Contractor Program.
                    We will review your details and contact you within 24 hours.
                  </p>
                  <button onClick={() => setIsSubmitted(false)} className="px-8 py-3 bg-[#ff674d] hover:bg-[#e65c45] text-white rounded-none font-bold transition-colors" data-id="element-1629">
                    Submit Another Application
                  </button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1630">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1631">
                    <div data-id="element-1632">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1633">
                        Company Name *
                      </label>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required className={inputClass} data-id="element-1634" />
                    </div>
                    <div data-id="element-1635">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1636">
                        Contact Name *
                      </label>
                      <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} required className={inputClass} data-id="element-1637" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1638">
                    <div data-id="element-1639">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1640">
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-1641" />
                    </div>
                    <div data-id="element-1642">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1643">
                        Phone Number *
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClass} data-id="element-1644" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1645">
                    <div data-id="element-1646">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1647">
                        Contractor License # (Optional)
                      </label>
                      <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} className={inputClass} data-id="element-1648" />
                    </div>
                    <div data-id="element-1649">
                      <label className="block text-sm font-bold text-stone-700 dark:text-stone-200 mb-2" data-id="element-1650">
                        Estimated Monthly Volume *
                      </label>
                      <select name="estimatedVolume" value={formData.estimatedVolume} onChange={handleChange} required className={inputClass + ' appearance-none'} data-id="element-1651">
                        <option value="" disabled data-id="element-1652">
                          Select volume...
                        </option>
                        <option value="1-5" data-id="element-1653">1-5 Deliveries</option>
                        <option value="6-15" data-id="element-1654">6-15 Deliveries</option>
                        <option value="16-30" data-id="element-1655">16-30 Deliveries</option>
                        <option value="31+" data-id="element-1656">31+ Deliveries</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-1657">
                    <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#ff674d] hover:bg-[#e65c45] text-white rounded-none font-bold text-lg transition-all group disabled:opacity-70" data-id="element-1658">
                      {isSubmitting ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1659" /> : <>
                          Submit Application
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1660" />
                        </>}
                    </button>
                  </div>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-16 bg-white dark:bg-[#2c2c2c]" data-id="element-1661">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1662">
          <div className="bg-stone-50 dark:bg-gradient-to-br dark:from-[#3c3c3c] dark:to-[#2c2c2c] rounded-2xl p-8 md:p-12 border border-stone-200 dark:border-[#4a4a4a] text-center shadow-2xl" data-id="element-1663">
            <h2 className="font-heading text-3xl font-bold text-stone-900 dark:text-white mb-4" data-id="element-1664">
              Need a Custom Quote?
            </h2>
            <p className="text-stone-600 dark:text-stone-400 mb-8 max-w-2xl mx-auto" data-id="element-1665">
              For orders over 100 yards, complex deliveries, or to set up a
              net-30 account, contact our dispatch team directly.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1666">
              <a href="tel:206-619-3742" className="flex items-center justify-center px-8 py-4 bg-[#ff674d] hover:bg-[#e65c45] text-white rounded-none font-bold text-lg transition-colors w-full sm:w-auto" data-id="element-1667">
                <Phone className="w-5 h-5 mr-2" data-id="element-1668" />
                (206) 619-3742
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Material Catalog - Collapsible */}
      <section className="py-20 bg-stone-50 dark:bg-[#1f1f1f]" data-id="element-1669">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1670">
          <button id="material-catalog" onClick={() => {
          const wasOpen = showCatalog;
          setShowCatalog(!showCatalog);
          if (wasOpen) {
            setTimeout(() => {
              const el = document.getElementById('material-catalog');
              if (el) {
                const navHeight = document.querySelector('nav')?.offsetHeight || 113;
                const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                window.scrollTo({
                  top,
                  behavior: 'smooth'
                });
              }
            }, 450);
          }
        }} className="w-full flex items-center justify-between border-b border-stone-200 dark:border-[#3f3f3f] pb-6 mb-8 group" data-id="element-1671">
            <div data-id="element-1672">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 dark:text-white text-left" data-id="element-1673">
                Material Catalog
              </h2>
              <p className="text-stone-600 dark:text-stone-500 text-sm mt-1 text-left" data-id="element-1674">
                {showCatalog ? 'Showing all materials' : 'Browse our full product lineup'}
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-1675">
              <span className="text-stone-600 dark:text-stone-300 text-sm hidden sm:block" data-id="element-1676">
                <Truck className="w-4 h-4 inline mr-1" data-id="element-1677" />
                Delivery Mon-Sat
              </span>
              <ChevronDown className={`w-6 h-6 text-stone-400 dark:text-stone-200 group-hover:text-[#ff674d] transition-all duration-300 ${showCatalog ? 'rotate-180' : ''}`} data-id="element-1678" />
            </div>
          </button>

          <AnimatePresence data-id="element-1679">
            {showCatalog && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-1680">
                <div className="space-y-16" data-id="element-1681">
                  {categories.map(category => <div key={category.name} data-id="element-1682">
                      <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-1683">
                        <span className="w-2 h-8 bg-[#ff674d] mr-4 rounded-sm" data-id="element-1684"></span>
                        {category.name}
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-1685">
                        {category.products.map((product, prodIndex) => <motion.div key={product.slug} initial={{
                    opacity: 0,
                    scale: 0.95
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: prodIndex % 4 * 0.1
                  }} className="bg-white dark:bg-[#2c2c2c] rounded-xl overflow-hidden border border-stone-200 dark:border-[#3f3f3f] hover:border-[#ff674d]/50 transition-all group flex flex-col" data-id="element-1686">
                            <div className="aspect-video overflow-hidden relative" data-id="element-1687">
                              <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1688" />
                              <div className="absolute top-3 right-3 bg-[#101010]/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-bold border border-white/10" data-id="element-1689">
                                ${product.pricePerYard}/yd
                              </div>
                            </div>

                            <div className="p-5 flex flex-col flex-grow" data-id="element-1690">
                              <h4 className="font-bold text-lg text-stone-900 dark:text-white mb-2" data-id="element-1691">
                                {product.name}
                              </h4>
                              <p className="text-stone-600 dark:text-stone-200 text-sm line-clamp-2 mb-6 flex-grow" data-id="element-1692">
                                {product.description}
                              </p>

                              <button onClick={() => navigate(`/shop/${product.slug}`)} className="w-full py-2.5 bg-stone-100 dark:bg-[#1f1f1f] hover:bg-[#ff674d] dark:hover:bg-[#ff674d] text-stone-900 dark:text-white hover:text-white rounded-lg font-medium transition-colors flex items-center justify-center group/btn" data-id="element-1693">
                                Order Material
                                <ArrowRight className="w-4 h-4 ml-2 opacity-0 -translate-x-2 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" data-id="element-1694" />
                              </button>
                            </div>
                          </motion.div>)}
                      </div>
                    </div>)}
                </div>
                <button onClick={() => {
              setShowCatalog(false);
              setTimeout(() => {
                const el = document.getElementById('material-catalog');
                if (el) {
                  const navHeight = document.querySelector('nav')?.offsetHeight || 113;
                  const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
                  window.scrollTo({
                    top,
                    behavior: 'smooth'
                  });
                }
              }, 450);
            }} className="mx-auto flex items-center gap-2 mt-12 py-3 px-6 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors text-sm font-bold uppercase tracking-wider border border-stone-300 dark:border-[#3f3f3f] hover:border-[#ff674d]/50 dark:hover:border-[#ff674d]/50 rounded-lg" data-id="element-1695">
                  <ChevronDown className="w-4 h-4 rotate-180" data-id="element-1696" />
                  Collapse Catalog
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </section>
    </main>;
}